import ip from 'ip';
import { IP_V4_SUBNET_REGEXP } from '@/common/utils/validate';
import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import CommonError from '@/common/utils/errors/CommonError';

type ValidationErrors = {
  empty: string;
  invalid: string;
  incorrect: (networkAddress: string, subnetMaskLength: string) => string;
}

export const VALIDATION_ERRORS = computedLocale<ValidationErrors>(() => ({
  empty: i18n.t('Specify a subnet CIDR'),
  invalid: i18n.t('Invalid subnet CIDR'),
  incorrect: (networkAddress, subnetMaskLength) => i18n.t('The correct CIDR is {cidr}', { cidr: `${networkAddress}/${subnetMaskLength}` })
}));

export function formatSubnet(subnetData): string {
  return `${subnetData.networkAddress}/${subnetData.subnetMaskLength}`;
}

export function formatIp(ipaddr = ''): string {
  return ipaddr.split('/')[0];
}

export function formatMask(ipaddr = ''): string {
  return ipaddr.split('/')[1];
}

export function cidrSubnet({ ipaddr = '', subnet = '', mask = '' }): string {
  let subnetInfo = '';
  if (ip) {
    try {
      // calculate subnetInfo by ip/maskLength or by subnetInfo && mask
      if (ipaddr) {
        subnetInfo = ip.cidrSubnet(ipaddr);
      } else if (subnet && mask) {
        subnetInfo = ip.subnet(subnet, mask);
      }
    } catch (e) {
      // mute error
    }
  }
  return subnetInfo;
}

export function validateSubnetCidr(value = ''): string | undefined {
  if (typeof value !== 'string') {
    throw new CommonError('Invalid type of input value. Argument should be a string');
  }
  if (!value) {
    // should't be empty
    return VALIDATION_ERRORS.empty;
  }

  if (!IP_V4_SUBNET_REGEXP.test(value)) {
    // no way when cannot pass the regexp validation
    return VALIDATION_ERRORS.invalid;
  }

  const { networkAddress, subnetMaskLength } = ip.cidrSubnet(value);
  if (networkAddress !== formatIp(value)) {
    // CIDR is set incorrectly
    return VALIDATION_ERRORS.incorrect(networkAddress, subnetMaskLength);
  }

  // value passed the validation
  return undefined;
}

export default function (subnets: Array<string> = []): Array<string> {
  return subnets.reduce((result: Array<string>, item, index) => {
    if (!item) {
      return result;
    }

    const subnet = /\//.test(item) ? item : `${item}/32`;
    if (!IP_V4_SUBNET_REGEXP.test(subnet)) {
      return result;
    }

    const subnetData = ip.cidrSubnet(subnet);
    if (!index) {
      return result.concat(formatSubnet(subnetData));
    }
    if (result.filter(it => subnetData.contains(ip.cidrSubnet(it).networkAddress)).length) {
      return result.filter(it => !subnetData.contains(ip.cidrSubnet(it).networkAddress))
        .concat(formatSubnet(subnetData));
    }
    if (result.find(it => ip.cidrSubnet(it).contains(subnetData.networkAddress))) {
      return result;
    }
    return result.concat(formatSubnet(subnetData));
  }, []);
}
